import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserService } from '@core/user/user.service';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { environment } from 'environments/environment';
import { catchError, map, Observable, of, ReplaySubject, switchMap, take, tap } from 'rxjs';

@Injectable({providedIn: 'root'})
export class NotificationsService
{
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]>
    {
        return this._notifications.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications
     */
    getAll(): Observable<Notification[]>
    {
        const userId = this._userService.user?.id;
        const baseUrl = environment.alertas.view_alertas;
        const url = userId
            ? `${baseUrl}?filter[receptor]=${userId}&sort=-fecha`
            : `${baseUrl}?sort=-fecha`;

        return this._httpClient.get<any>(url).pipe(
            map((response) =>
            {
                const result = Array.isArray(response?.result)
                    ? response.result
                    : Array.isArray(response)
                        ? response
                        : [];

                return result.map((alerta: any, index: number) =>
                {
                    const rawTitle = (alerta?.tipo_descripcion ?? alerta?.titulo ?? alerta?.descripcion_alerta ?? alerta?.alerta_descripcion ?? '').toString().trim();
                    const rawDescription = (alerta?.detalle ?? alerta?.mensaje ?? alerta?.descripcion ?? alerta?.observaciones ?? '').toString().trim();
                    const title = rawTitle || 'Alerta generica';
                    const description = rawDescription;
                    const time = (alerta?.fecha ?? alerta?.fecha_hora ?? alerta?.created_at ?? new Date().toISOString()).toString();
                    const readValue = alerta?.read ?? alerta?.leido ?? alerta?.Leido ?? alerta?.LEIDO ?? 0;
                    const read = readValue === true || readValue === 1 || readValue === '1' || readValue === 'Si';
                    const id = (alerta?.codigo ?? alerta?.Codigo ?? alerta?.id ?? alerta?.ID ?? alerta?.idalerta ?? alerta?.alerta ?? index).toString();

                    return {
                        id,
                        icon: alerta?.icono ?? alerta?.icon ?? 'heroicons_outline:chat-bubble-left-right',
                        title,
                        description,
                        time,
                        link: alerta?.url ?? alerta?.link ?? alerta?.enlace,
                        useRouter: false,
                        read,
                    } as Notification;
                });
            }),
            tap((notifications) =>
            {
                this._notifications.next(notifications);
            }),
            catchError((error) =>
            {
                console.error('NotificationsService.getAll error:', error);
                this._notifications.next([]);
                return of([]);
            }),
        );
    }

    /**
     * Create a notification
     *
     * @param notification
     */
    create(notification: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.post<Notification>('api/common/notifications', {notification}).pipe(
                map((newNotification) =>
                {
                    // Update the notifications with the new notification
                    this._notifications.next([...notifications, newNotification]);

                    // Return the new notification from observable
                    return newNotification;
                }),
            )),
        );
    }

    /**
     * Update the notification
     *
     * @param id
     * @param notification
     */
    update(id: string, notification: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.patch<Notification>('api/common/notifications', {
                id,
                notification,
            }).pipe(
                map((updatedNotification: Notification) =>
                {
                    // Find the index of the updated notification
                    const index = notifications.findIndex(item => item.id === id);

                    // Update the notification
                    notifications[index] = updatedNotification;

                    // Update the notifications
                    this._notifications.next(notifications);

                    // Return the updated notification
                    return updatedNotification;
                }),
            )),
        );
    }

    /**
     * Delete the notification
     *
     * @param id
     */
    delete(id: string): Observable<boolean>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.delete<boolean>('api/common/notifications', {params: {id}}).pipe(
                map((isDeleted: boolean) =>
                {
                    // Find the index of the deleted notification
                    const index = notifications.findIndex(item => item.id === id);

                    // Delete the notification
                    notifications.splice(index, 1);

                    // Update the notifications
                    this._notifications.next(notifications);

                    // Return the deleted status
                    return isDeleted;
                }),
            )),
        );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<any>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications =>
            {
                const notificaciones = (notifications || [])
                    .filter((notification) => !notification.read)
                    .map((notification) =>
                    {
                        const codigo = Number(notification.id);
                        return Number.isFinite(codigo) ? { codigo } : null;
                    })
                    .filter(Boolean);

                return this._httpClient.put<any>(
                    environment.alertas.alerta_usuario_marcar_leidas,
                    { notificaciones },
                ).pipe(
                    map((response: any) =>
                    {
                        // Go through all notifications and set them as read
                        notifications.forEach((notification, index) =>
                        {
                            notifications[index].read = true;
                        });

                        // Update the notifications
                        this._notifications.next(notifications);

                        // Return the response
                        return response;
                    }),
                );
            }),
        );
    }

    /**
     * Mark a single notification as read
     *
     * @param notification
     */
    markAsRead(notification: Notification): Observable<any>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications =>
            {
                const codigo = Number(notification.id);
                const notificaciones = Number.isFinite(codigo) ? [{ codigo }] : [];

                return this._httpClient.put<any>(
                    environment.alertas.alerta_usuario_marcar_leidas,
                    { notificaciones },
                ).pipe(
                map((response: any) =>
                {
                    const index = notifications.findIndex(item => item.id === notification.id);
                    if ( index > -1 )
                    {
                        notifications[index].read = true;
                        this._notifications.next(notifications);
                    }
                    return response;
                }),
                );
            }),
        );
    }
}
